import { Undo } from '@mui/icons-material';
import { Tab, Tabs, Typography } from '@mui/material';
import { FunctionComponent, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import ScrollButton from '@/components/buttons/scroll-button';
import CustomDateTimePicker from '@/components/pickers/date-time-range-picker';
import * as Constants from '@/constants';
import { TimepickerContext, timeSelectionIntervals } from '@/contexts/timepicker';
import { timeFormatyyyyMMdhmm } from '@/helpers/helper-functions';

const useStyles = makeStyles()((theme) => ({
  timeSelector: {
    minWidth: 60,
    maxWidth: 60,
  },
  customTimeIcon: {
    height: '16px',
  },
  selectedTimeText: {
    color: '#27aae1',
  },
  timePickerTabs: {
    overflow: 'hidden',
    '-ms-overflow-style': 'none',
    '&::-webkit-scrollbar': {
      width: '0 !important',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
    },
  },
  tabsScroller: {
    '-ms-overflow-style': 'none',
    '&::-webkit-scrollbar': {
      width: '0 !important',
    },
  },
  timePickerSelectedTime: {
    marginLeft: '1em',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
    [theme.breakpoints.down('lg')]: {
      marginTop: '0.5em',
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: '1em',
    },
  },
  flex: {
    flex: 1,
    overflow: 'hidden',
    marginTop: theme.spacing(-1),
  },
  hiddenOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  hidden: {
    minWidth: 60,
    maxWidth: 60,
    visibility: 'hidden',
  },
}));

const SelectedText: React.FC<{ from: Date; to: Date | null }> = ({ from, to }) => {
  const { classes } = useStyles();
  const [t, i18n] = useTranslation(['shared']);

  return (
    <Typography className={classes.timePickerSelectedTime} variant="body2">
      <span className={classes.hiddenOnMobile}>{t(['shared:selected'], { defaultValue: 'Selected' })}: </span>
      <span className={classes.selectedTimeText}>{timeFormatyyyyMMdhmm(from, i18n.language)}</span>{' '}
      {t(['shared:to'], { defaultValue: 'to' }).toLowerCase()}{' '}
      <span className={classes.selectedTimeText}>
        {to ? timeFormatyyyyMMdhmm(to, i18n.language) : t(['shared:now'], { defaultValue: 'now' }).toLowerCase()}
      </span>
    </Typography>
  );
};

interface Properties {
  hideSelected?: boolean;
  includeLargeRanges?: boolean;
}

const TimePicker: FunctionComponent<Properties> = ({ hideSelected = false, includeLargeRanges }) => {
  const { from, to, setTime, historySize, undo, relativeTimeInterval } = useContext(TimepickerContext);

  const { classes } = useStyles();
  const [t] = useTranslation(['shared']);

  const calendarIconRef = useRef(null);

  const [open, setOpen] = useState(false);

  const filteredTimeSelectionIntervals = useMemo(
    () =>
      timeSelectionIntervals.filter(
        ({ largeRange }) => largeRange === undefined || (includeLargeRanges && largeRange === true),
      ),
    [includeLargeRanges],
  );

  const onTimeSelect = useCallback(
    (_: unknown, v: string) => {
      const selectedTimeInterval = filteredTimeSelectionIntervals[Number(v)];
      if (!selectedTimeInterval) {
        return;
      }

      setTime(new Date(Date.now() - selectedTimeInterval.time), null);
    },
    [setTime, filteredTimeSelectionIntervals],
  );

  const onClickCalendarIcon = useCallback(() => setOpen(true), [setOpen]);
  const onClose = useCallback(() => setOpen(false), [setOpen]);
  const onCalendarSelect = useCallback<typeof setTime>((start, end) => {
    setTime(start, end);
    onClose();
  }, []);

  const numSelectableTabs = filteredTimeSelectionIntervals.length + 1;

  // relativeTimeInverval is not 100% exact, so +-1 second
  const value =
    ((filteredTimeSelectionIntervals.findIndex(
      ({ time }) => relativeTimeInterval && Math.abs(relativeTimeInterval - time) < 1000,
    ) %
      numSelectableTabs) +
      numSelectableTabs) %
    numSelectableTabs;

  return (
    <div className={classes.flex}>
      <Tabs
        indicatorColor="primary"
        className={classes.timePickerTabs}
        classes={{ scroller: classes.tabsScroller }}
        value={value}
        onChange={onTimeSelect}
        variant="scrollable"
        scrollButtons
        ScrollButtonComponent={ScrollButton}
        allowScrollButtonsMobile
      >
        {filteredTimeSelectionIntervals.map(({ label }) => {
          return (
            <Tab className={classes.timeSelector} key={label} label={t([`shared:${label}`], { defaultValue: label })} />
          );
        })}
        <Tab
          className={classes.timeSelector}
          key="custom"
          icon={<Constants.CALENDAR_PICKER_ICON className={classes.customTimeIcon} />}
          ref={calendarIconRef}
          onClick={onClickCalendarIcon}
        />
        <Tab
          className={!!historySize ? classes.timeSelector : classes.hidden}
          key="time-history"
          icon={<Undo className={classes.customTimeIcon} />}
          onClick={undo}
        />
      </Tabs>
      {!hideSelected && <SelectedText from={from} to={to} />}
      <CustomDateTimePicker
        anchorEl={calendarIconRef.current}
        disableFuture
        from={from}
        to={to || new Date()}
        open={open}
        closePicker={onClose}
        setTime={onCalendarSelect}
        range
        showTabs
      />
    </div>
  );
};

export default TimePicker;
